import {
  DollarSign, UtensilsCrossed, TrendingUp 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import type { Recipe } from '@/features/recipes';

interface CostingViewProps {
  readonly recipes: Recipe[];
  readonly totalCount: number;
}

export function CostingView({
  recipes, totalCount 
}: CostingViewProps) {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Avg Food Cost %
            </CardTitle>
            <DollarSign className="h-5 w-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">28.5%</div>
            <p className="text-xs text-muted-foreground">Target: 30%</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Recipes Costed
            </CardTitle>
            <UtensilsCrossed className="h-5 w-5 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalCount}</div>
            <p className="text-xs text-muted-foreground">100% coverage</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Price Alerts
            </CardTitle>
            <TrendingUp className="h-5 w-5 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">3</div>
            <p className="text-xs text-muted-foreground">Ingredients above target</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recipe Cost Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="py-3 text-left font-medium">Recipe</th>
                  <th className="py-3 text-left font-medium">Type</th>
                  <th className="py-3 text-right font-medium">Portion Cost</th>
                  <th className="py-3 text-right font-medium">Sell Price</th>
                  <th className="py-3 text-right font-medium">Food Cost %</th>
                </tr>
              </thead>
              <tbody>
                {recipes.slice(0, 10).map((recipe) => {
                  const portionCost = recipe.costPerPortion ?? 0;
                  const sellPrice = portionCost * 3.5;
                  const foodCostPct = sellPrice > 0 ? (portionCost / sellPrice) * 100 : 0;
                  const costClass = foodCostPct > 30 ? 'text-red-600' : 'text-green-600';
                  return (
                    <tr key={recipe.recipeId} className="border-b">
                      <td className="py-3">{recipe.name}</td>
                      <td className="py-3 capitalize">{recipe.type}</td>
                      <td className="py-3 text-right">${portionCost.toFixed(2)}</td>
                      <td className="py-3 text-right">${sellPrice.toFixed(2)}</td>
                      <td className={`py-3 text-right font-medium ${costClass}`}>
                        {foodCostPct.toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
